/**
 * Results Summary Component
 * Displayed after assessment completion
 */

import React from 'react';
import { AttemptResults } from '../../types/models';

interface ResultsSummaryProps {
  results: AttemptResults;
  showQuestionReview?: boolean; // Deprecated: now uses result_settings.show_question_review
  onRetry?: () => void;
  onReturn?: () => void;
}

// Finds the [label](url) starting at `from`, tracking paren depth so that URLs
// containing their own (possibly nested) parentheses are captured in full.
// Returns null if there's no well-formed link starting there.
function matchLinkAt(
  text: string,
  from: number,
): { label: string; url: string; end: number } | null {
  if (text[from] !== '[') return null;
  const labelEnd = text.indexOf(']', from + 1);
  if (labelEnd === -1 || text[labelEnd + 1] !== '(') return null;

  const urlStart = labelEnd + 2;
  let depth = 1;
  let i = urlStart;
  while (i < text.length && depth > 0) {
    if (text[i] === '(') depth++;
    else if (text[i] === ')') depth--;
    if (depth > 0) i++;
  }
  if (depth !== 0) return null;

  const url = text.slice(urlStart, i);
  if (!/^https?:\/\//.test(url)) return null;

  return { label: text.slice(from + 1, labelEnd), url, end: i + 1 };
}

// Renders plain text, turning any [label](url) links into real anchor tags.
function renderTextWithLinks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let searchFrom = 0;
  while (searchFrom < text.length) {
    const bracketIndex = text.indexOf('[', searchFrom);
    if (bracketIndex === -1) break;

    const link = matchLinkAt(text, bracketIndex);
    if (!link) {
      searchFrom = bracketIndex + 1;
      continue;
    }

    if (bracketIndex > lastIndex) parts.push(text.slice(lastIndex, bracketIndex));
    parts.push(
      <a key={key++} href={link.url} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
    lastIndex = link.end;
    searchFrom = link.end;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export const ResultsSummary: React.FC<ResultsSummaryProps> = ({
  results,
  showQuestionReview: propShowQuestionReview, // Deprecated: kept for backward compatibility
  onRetry,
  onReturn,
}) => {
  const formatTime = (seconds: number | null): string => {
    if (seconds === null) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const hasPassFail = results.passed !== null;
  const resultSettings = results.attempt?.assessment?.result_settings || {};
  const showScoreAndPercentage = resultSettings.show_score_and_percentage !== false; // Default true
  const showBestCategories = resultSettings.show_best_categories === true;
  const showWorstCategories = resultSettings.show_worst_categories === true;
  const showAllCategories = resultSettings.show_all_categories === true;
  const numCategoriesToShow = resultSettings.num_categories_to_show || 3;
  // Use result_settings as primary source, fallback to deprecated prop for backward compatibility
  const showQuestionReview = resultSettings.show_question_review === true || 
    (resultSettings.show_question_review === undefined && propShowQuestionReview === true);
  const showCorrectAnswers = resultSettings.show_correct_answers === true; // Default false
  const showExplanations = resultSettings.show_explanations === true; // Default false
  const completionButtonText = resultSettings.completion_button_text || 'Return to Dashboard';
  const completionButtonUrl = resultSettings.completion_button_url;
  const postAssessmentInstructions = resultSettings.post_assessment_instructions;
  
  // Get category scores
  const categoryScores = results.category_scores || [];
  const bestCategories = [...categoryScores]
    .sort((a, b) => b.score_percent - a.score_percent)
    .slice(0, numCategoriesToShow);
  const worstCategories = [...categoryScores]
    .sort((a, b) => a.score_percent - b.score_percent)
    .slice(0, numCategoriesToShow);

  let completionButton: React.ReactNode = null;
  if (completionButtonUrl) {
    completionButton = (
      <a href={completionButtonUrl} className="btn btn-primary">
        {completionButtonText}
      </a>
    );
  } else if (onReturn) {
    completionButton = (
      <button onClick={onReturn} className="btn btn-primary">
        {completionButtonText}
      </button>
    );
  }

  return (
    <div className="results-summary">
      <div className="results-header">
        <h2>Assessment Complete</h2>
      </div>

      {showScoreAndPercentage ? (
        // Score and percentage view
        <div className="score-section">
          <div className="score-display">
            <div className="score-main">
              <span className="score-raw">{results.score_raw}</span>
              <span className="score-separator">/</span>
              <span className="score-max">{results.score_max}</span>
            </div>
            <div className="score-percent">{results.score_percent.toFixed(1)}%</div>
          </div>

          <div className="score-details">
            {hasPassFail && (
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className={`value ${results.passed ? 'passed' : 'failed'}`}>
                  {results.passed ? 'Passed ✓' : 'Failed ✗'}
                </span>
              </div>
            )}
            <div className="detail-item">
              <span className="label">Completed:</span>
              <span className="value">
                {results.attempt?.completed_at
                  ? new Date(results.attempt.completed_at).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Time Spent:</span>
              <span className="value">{formatTime(results.time_spent_seconds)}</span>
            </div>
          </div>
        </div>
      ) : (
        // No score display - show completion message only
        <div className="completion-section">
          <div className="completion-message">
            <div className="completion-icon">✓</div>
            <h3>Thank you for completing this assessment!</h3>
            <p>Your responses have been recorded.</p>
          </div>
          <div className="completion-details">
            <div className="detail-item">
              <span className="label">Time Spent:</span>
              <span className="value">{formatTime(results.time_spent_seconds)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Completed:</span>
              <span className="value">
                {results.attempt?.completed_at
                  ? new Date(results.attempt.completed_at).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Category Performance Sections */}
      {(showBestCategories || showWorstCategories || showAllCategories) && categoryScores.length > 0 && (
        <div className="category-performance-section">
          {showBestCategories && bestCategories.length > 0 && (
            <div className="best-categories">
              <h3>Your Strongest Areas</h3>
              <div className="category-list">
                {bestCategories.map((cat) => (
                  <div key={cat.category} className="category-item">
                    <span className="category-name">{cat.category}</span>
                    <span className="category-score">
                      {cat.score_earned.toFixed(1)} / {cat.score_possible.toFixed(1)} ({cat.score_percent.toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showWorstCategories && worstCategories.length > 0 && (
            <div className="worst-categories">
              <h3>Areas for Improvement</h3>
              <div className="category-list">
                {worstCategories.map((cat) => (
                  <div key={cat.category} className="category-item">
                    <span className="category-name">{cat.category}</span>
                    <span className="category-score">
                      {cat.score_earned.toFixed(1)} / {cat.score_possible.toFixed(1)} ({cat.score_percent.toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showAllCategories && categoryScores.length > 0 && (
            <div className="all-categories">
              <h3>All Category Results</h3>
              <div className="category-list">
                {categoryScores
                  .sort((a, b) => b.score_percent - a.score_percent) // Sort by score descending
                  .map((cat) => (
                    <div key={cat.category} className="category-item">
                      <span className="category-name">{cat.category}</span>
                      <span className="category-score">
                        {cat.score_earned.toFixed(1)} / {cat.score_possible.toFixed(1)} ({cat.score_percent.toFixed(1)}%)
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showQuestionReview && results.results_by_question.length > 0 && (
        <div className="question-review">
          <h3>Question Review</h3>
          <div className="questions-list">
            {results.results_by_question.map((result, index) => {
              const question = result.question;
              if (!question) return null;

              // Check if this is a rating scale question (has partial credit options strictly between 0 and 1)
              const isRatingScale = question.answer_options.some(
                (opt) => opt.partial_credit_weight > 0 && opt.partial_credit_weight < 1
              );

              // Get selected options text
              const selectedOptions = question.answer_options
                .filter((opt) => result.selected_option_ids?.includes(opt.id))
                .map((opt) => opt.option_text);

              // Get correct options text (only if show_correct_answers is true)
              const correctOptions = showCorrectAnswers
                ? question.answer_options
                    .filter((opt) => opt.is_correct)
                    .map((opt) => opt.option_text)
                : [];

              let questionResultClass = 'neutral';
              if (!isRatingScale && showCorrectAnswers && hasPassFail) {
                questionResultClass = result.is_correct ? 'correct' : 'incorrect';
              }

              return (
                <div
                  key={result.question_id}
                  className={`question-result ${questionResultClass}`}
                >
                  <div className="question-header">
                    <span className="question-number">Question {index + 1}</span>
                    <span className="question-points">
                      {result.points_earned} / {question.points_possible || 0} points
                    </span>
                    {!isRatingScale && hasPassFail && showCorrectAnswers && (
                      <span className={`result-badge ${result.is_correct ? 'correct' : 'incorrect'}`}>
                        {result.is_correct ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    )}
                  </div>
                  <div className="question-text">{question.prompt_text}</div>
                  
                  {/* Show user's response */}
                  <div className="response-details">
                    {question.question_type === 'short_answer' ? (
                      <div>
                        <strong>Your answer:</strong> {result.text_response || 'No response'}
                      </div>
                    ) : (
                      <div>
                        <strong>Selected:</strong>{' '}
                        {selectedOptions.length > 0
                          ? selectedOptions.join(', ')
                          : 'No selection'}
                      </div>
                    )}
                    
                    {/* Show correct answer only if show_correct_answers is true */}
                    {showCorrectAnswers && correctOptions.length > 0 && (
                      <div className="correct-answer">
                        <strong>Correct answer:</strong> {correctOptions.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  {/* Show explanation only if show_explanations is true */}
                  {showExplanations && question.explanation && (
                    <div className="question-explanation">{question.explanation}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {postAssessmentInstructions && (
        <div className="post-assessment-instructions">
          <p style={{ whiteSpace: 'pre-line' }}>{renderTextWithLinks(postAssessmentInstructions)}</p>
        </div>
      )}

      <div className="results-actions">
        {onRetry && (
          <button onClick={onRetry} className="btn btn-secondary">
            Try Again
          </button>
        )}
        {completionButton}
      </div>
    </div>
  );
};
